import { Flags } from '@/apis/family_application/requests'
import { LogUserActivity } from '@/apis/users/requests'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { formatPhoneNumber } from '@/app/common/utilities'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { FamilyApplicationRoutes } from '@/app/pages/family_application/routing/family-application.routing.types.ts'
import { Button } from '@/common/components/Button'
import { SuccessIcon } from '@/common/icons/general'
import { FamilyApplicationData } from '@/layouts/FamilyApplicationLayout'
import axiosInstance from '@/libs/axios_config'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { useMutation } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { LOG_TYPES } from '../../../constants'
import useFamilyApplicationRouting from '../../../routing/useFamilyApplicationRouting'
import { hasRenewalStudents } from '../../../utils'

interface CreateFlagsRequest {
  programId: number
  flagType: Flags
}

// todo break this request out into a separate file for re-usability.
async function createFlag({ programId, flagType }: CreateFlagsRequest) {
  const endpoint = `/family_application/flags`
  const params = new URLSearchParams({
    application_id: programId.toString(),
    flag_code: flagType,
  })

  return await axiosInstance.post(`${endpoint}?${params}`)
}

export const IdentitySuccessVerified = () => {
  const { t: translate } = useTranslation()
  const applicantDetails = useOutletContext<FamilyApplicationData>()
  const familyApplication = useSelector(selectFamilyApplication)
  const { onModuleEnd } = useFamilyApplicationRouting(familyApplication)
  const navigate = useNavigate()
  const { fireAlert } = useAppAlert()

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Identity Verification Complete page',
      })
      hasLoggedPageView.current = true;
    }
  }, []) 

  const clearMeFlagMutation = useMutation({
    mutationFn: () =>
      createFlag({
        flagType: 'clearme_data_inaccurate', // Este es el nuevo flagType
        programId: familyApplication.application_id, // El ID del programa
      }),
  })

  const flagMutation = useMutation({
    mutationFn: () =>
      createFlag({
        flagType: 'identity',
        // #TODO. Fix this.
        programId: familyApplication.application_id,
      }),
  })

  async function handleSubmit() {
    let nextRoute: FamilyApplicationRoutes
    const residencyFlag = familyApplication.flags.find(
      (flag) => flag.flag_code === 'residency'
    )

    if (residencyFlag) {
      await fireAlert({
        variant: 'warning',
        message: translate('identity_summary_page.residency_flag_detected'),
      })
      nextRoute = AppRoutePaths.family_application.residency_verification.index
    } else {
      nextRoute = hasRenewalStudents(applicantDetails.familyApplication)
        ? AppRoutePaths.family_application.student_details.additional_student
        : AppRoutePaths.family_application.student_details.index
    }

    navigate(onModuleEnd(nextRoute))
  }

  function handleReject() {
    flagMutation.mutate()
    clearMeFlagMutation.mutate()

    navigate(
      AppRoutePaths.family_application.identity_verification.verify_information
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Content className="items-center justify-center">
        <div className="mb-8 text-center flex flex-col items-center">
          <SuccessIcon className="h-12 w-12 text-green-700 " />
          <h1 className="text-3xl font-medium text-gray-900">
            {translate('identity_verification_complete')}
          </h1>
        </div>
        <Card className="w-full flex-shrink-0">
          <CardHeader className="font-medium text-md">
            {translate('information')}
          </CardHeader>
          <CardContent className="w-full">
            <ul>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('name')}</span>
                <span className="text-neutral-900 font-medium">
                  {familyApplication.first_name} {familyApplication.last_name}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('address')}</span>
                <span className="text-neutral-900 font-medium">
                  {familyApplication.address}
                </span>
              </li>
              {familyApplication.address2 && (
                <li className="flex items-center justify-between py-2">
                  <span className="text-neutral-700">
                    {translate('address_2')}
                  </span>
                  <span className="text-neutral-900 font-medium">
                    {familyApplication.address2}
                  </span>
                </li>
              )}
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('city')}</span>
                <span className="text-neutral-900 font-medium">
                  {familyApplication.city}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('state')}</span>
                <span className="text-neutral-900 font-medium">
                  {
                    applicantDetails.states.data.find((state) => {
                      return state.id === familyApplication.global_state_id
                    })?.name
                  }
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('zip')}</span>
                <span className="text-neutral-900 font-medium">
                  {familyApplication.zip_code}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">{translate('phone')}</span>
                <span className="text-neutral-900 font-medium">
                  {formatPhoneNumber(familyApplication.phone?.toString() ?? '')}
                </span>
              </li>
              <li className="flex items-center justify-between py-2">
                <span className="text-neutral-700">
                  {translate('date_of_birth_capital')}
                </span>
                <span className="text-neutral-900 font-medium">
                  {familyApplication.date_of_birth
                    ? format(
                        parseISO(familyApplication.date_of_birth),
                        'MM-dd-y'
                      )
                    : ''}
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>
        <div className="w-full flex flex-col gap-4 mt-4">
          <Button
            onClick={() => {
              handleSubmit()
            }}
            className="h-9"
            variant="primary"
          >
            {translate(
              'i_certify_that_the_information_provided_is_true_and_accurate'
            )}
          </Button>
          <Button
            onClick={() => {
              handleReject()
            }}
            className="h-9"
            variant="primary"
          >
            {translate('i_reject_this_information_is_true_and_accurate')}
          </Button>
        </div>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="flex-col">
        <span className="text-gray-600 mb-2 text-center"></span>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
